import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { BaseDialog } from '@app/components/BaseDialog/BaseDialog';
import { Button } from '@app/components/Button/Button';
import CloseIcon from '@app/assets/icons/close.svg?react';

interface NavigationContextType {
  showConfirmationModal: boolean;
  pendingNavigation: string | null;
  interceptNavigation: (targetPath: string) => void;
  handleConfirmNavigation: () => void;
  handleCancelNavigation: () => void;
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

export const useNavigationContext = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigationContext must be used within a NavigationProvider');
  }
  return context;
};

interface NavigationProviderProps {
  children: ReactNode;
  navigate: (path: string) => void;
  location: { pathname: string };
}

export const NavigationProvider: React.FC<NavigationProviderProps> = ({ children, navigate, location }) => {
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);



  // Check if we're in campaign creation flow
  const isInCampaignCreation = useCallback(() => {
    return location.pathname.includes('/campaigns/create');
  }, [location.pathname]);

  // Intercept navigation attempts
  const interceptNavigation = useCallback((targetPath: string) => {
    const isNavigatingWithinCampaignCreation = targetPath.includes('/campaigns/create');
    
    // Show confirmation if navigating away from campaign creation flow
    if (isInCampaignCreation() && !isNavigatingWithinCampaignCreation) {
      setPendingNavigation(targetPath);
      setShowConfirmationModal(true);
    } else {
      navigate(targetPath);
    }
  }, [isInCampaignCreation, navigate]);

  // Handle confirmation modal actions
  const handleConfirmNavigation = useCallback(() => {
    if (pendingNavigation) {
      setShowConfirmationModal(false);
      setPendingNavigation(null);
      navigate(pendingNavigation);
    }
  }, [pendingNavigation, navigate]);

  const handleCancelNavigation = useCallback(() => {
    setShowConfirmationModal(false);
    setPendingNavigation(null);
  }, []);

  const value: NavigationContextType = {
    showConfirmationModal,
    pendingNavigation,
    interceptNavigation,
    handleConfirmNavigation,
    handleCancelNavigation,
  };

  return (
    <NavigationContext.Provider value={value}>
      {children}
      
      <BaseDialog
        open={showConfirmationModal}
        onClose={handleCancelNavigation}
      >
        <div className="border-b border-b-base-ghostWhite p-6 mb-6 flex gap-2 items-start justify-between">
          <div className="bg-base-antiFlash flex items-center p-2 rounded-lg">
            <CloseIcon width={24} height={24} />
          </div>
          <div className="mr-auto my-auto">
            <h5 className="font-bold text-lg">Unsaved Changes</h5>
          </div>
          <div>
            <button type="button" onClick={handleCancelNavigation}>
              <CloseIcon />
            </button>
          </div>
        </div>
        <div className="px-6 pb-4">
          <p className="text-base-davyGray">
            You have unsaved changes in your campaign. Are you sure you want to leave this page? 
          </p>
          <div className="flex items-center gap-4 justify-center py-4">
            <Button
              kind="secondary"
              className="w-full"
              type="button"
              onClick={handleCancelNavigation}
            >
              Cancel
            </Button>
            <Button
              className="w-full"
              type="button"
              onClick={handleConfirmNavigation}
            >
              Leave Page
            </Button>
          </div>
        </div>
      </BaseDialog>
    </NavigationContext.Provider>
  );
}; 